import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBook, FiUsers, FiZap, FiStar,
  FiPlay, FiLock, FiCheck, FiChevronRight, FiClock,
  FiBarChart2, FiCpu, FiGlobe,
  FiPenTool, FiMusic, FiFilm, FiCode, FiDollarSign, FiActivity
} from 'react-icons/fi';
import './Academy.css';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { getAcademyCourses } from './catalog';

const Academy = () => {
  const { isAuthenticated } = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const [userStats, setUserStats] = useState({
    xp: 0,
    level: 1,
    streak: 0,
    lives: 5,
    gems: 0,
    lessonsToday: 0,
    dailyGoal: 3
  });

  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [dailyChallenge, setDailyChallenge] = useState(null);

  const [courses, setCourses] = useState([]);

  const categoryMeta = {
    todos: { id: 'todos', name: 'Todos', icon: FiBook, color: '#6366f1' },
    ciencias: { id: 'ciencias', name: 'Ciencias', icon: FiCpu, color: '#10b981' },
    matematicas: { id: 'matematicas', name: 'Matemáticas', icon: FiBarChart2, color: '#f59e0b' },
    idiomas: { id: 'idiomas', name: 'Idiomas', icon: FiGlobe, color: '#3b82f6' },
    arte: { id: 'arte', name: 'Arte y Diseño', icon: FiPenTool, color: '#ec4899' },
    musica: { id: 'musica', name: 'Música', icon: FiMusic, color: '#8b5cf6' },
    tecnologia: { id: 'tecnologia', name: 'Tecnología', icon: FiCode, color: '#06b6d4' },
    negocios: { id: 'negocios', name: 'Negocios', icon: FiDollarSign, color: '#84cc16' },
    salud: { id: 'salud', name: 'Salud', icon: FiActivity, color: '#ef4444' },
    cine: { id: 'cine', name: 'Cine', icon: FiFilm, color: '#f97316' }
  };

  const [categories, setCategories] = useState(Object.values(categoryMeta));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await academyAPI.getDashboard();
        if (data?.userStats) setUserStats(data.userStats);
        if (Array.isArray(data?.courses) && data.courses.length > 0) {
          setCourses(data.courses);
        } else {
          setCourses(getAcademyCourses().map((c) => ({
            id: c.slug,
            slug: c.slug,
            title: c.title,
            description: c.description,
            category: c.category,
            difficulty: c.difficulty,
            duration: c.duration,
            lessons: (c.units || []).reduce((acc, u) => acc + (u.lessons?.length || 0), 0),
            completedLessons: 0,
            xpReward: Number(c.xpReward || 0),
            enrolled: 0,
            rating: null,
            image: c.image,
            instructor: c.instructor,
            hasGame: !!c.hasGame,
            featured: !!c.featured
          })));
        }
        if (data?.dailyChallenge) setDailyChallenge(data.dailyChallenge);
        if (Array.isArray(data?.categories)) {
          const mapped = data.categories
            .map((id) => categoryMeta[id] || { id, name: id, icon: FiBook, color: '#6366f1' });
          setCategories(mapped);
        }
      } catch (e) {
        console.error('Error loading academy dashboard:', e);
        // Fallback to local catalog so courses are always visible
        setCourses(getAcademyCourses().map((c) => ({
          id: c.slug,
          slug: c.slug,
          title: c.title,
          description: c.description,
          category: c.category,
          difficulty: c.difficulty,
          duration: c.duration,
          lessons: (c.units || []).reduce((acc, u) => acc + (u.lessons?.length || 0), 0),
          completedLessons: 0,
          xpReward: Number(c.xpReward || 0),
          enrolled: 0,
          rating: null,
          image: c.image,
          instructor: c.instructor,
          hasGame: !!c.hasGame,
          featured: !!c.featured
        })));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const filteredCourses = selectedCategory === 'todos' 
    ? courses 
    : courses.filter(c => c.category === selectedCategory);

  const getProgressPercentage = (completed, total) => Math.round((completed / total) * 100);

  const getLevelProgress = () => {
    const level = Math.max(1, Number(userStats.level || 1));
    const xpForNextLevel = level * 500;
    const currentLevelXp = Number(userStats.xp || 0) % 500;
    return (currentLevelXp / xpForNextLevel) * 100;
  };

  return (
    <div className="academy-page">
      {/* Header con stats del usuario */}
      <header className="academy-header">
        <div className="academy-header-inner">
          <div className="header-left">
            <div className="academy-logo">
              <FiBook className="logo-icon" />
              <div className="logo-text">
                <span className="logo-title">GRAPHYRA</span>
                <span className="logo-subtitle">ACADEMIA</span>
              </div>
            </div>
          </div>

          {isAuthenticated && (
          <div className="header-stats">
          {/* Racha */}
          <button
            className="stat-item streak"
            onClick={() => {
              if (!isAuthenticated) return setShowLoginPrompt(true);
              setShowStreakModal(true);
            }}
          >
            <span className="stat-icon">🔥</span>
            <span className="stat-value">{userStats.streak}</span>
            <span className="stat-label">días</span>
          </button>

          {/* Gemas */}
          <div className="stat-item gems">
            <span className="stat-icon">💎</span>
            <span className="stat-value">{userStats.gems}</span>
          </div>

          {/* Vidas */}
          <div className="stat-item lives">
            <span className="stat-icon">❤️</span>
            <span className="stat-value">{userStats.lives}</span>
          </div>

          {/* XP y Nivel */}
          <div className="stat-item level">
            <div className="level-badge">
              <FiStar />
              <span>{userStats.level}</span>
            </div>
            <div className="xp-bar">
              <div className="xp-fill" style={{ width: `${getLevelProgress()}%` }}></div>
            </div>
            <span className="xp-text">{userStats.xp} XP</span>
          </div>
          </div>
          )}

          <div className="header-right" />
        </div>
      </header>

      {!isAuthenticated && showLoginPrompt && (
        <LoginPrompt
          message="Inicia sesión para guardar tu progreso, racha y recompensas"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      {/* Progreso diario (solo logueado) */}
      {isAuthenticated && (
      <section className="daily-progress">
        <div className="progress-card">
          <div className="progress-header">
            <h3>🎯 Meta diaria</h3>
            <span className="progress-count">{userStats.lessonsToday}/{userStats.dailyGoal} lecciones</span>
          </div>
          <div className="daily-bar">
            {[...Array(userStats.dailyGoal)].map((_, i) => (
              <div 
                key={i} 
                className={`daily-dot ${i < userStats.lessonsToday ? 'completed' : ''}`}
              >
                {i < userStats.lessonsToday && <FiCheck />}
              </div>
            ))}
          </div>
          <p className="progress-motivation">
            {userStats.lessonsToday >= userStats.dailyGoal 
              ? '¡Meta cumplida! 🎉 Sigue así' 
              : `¡Solo ${userStats.dailyGoal - userStats.lessonsToday} lección más para tu meta!`
            }
          </p>
        </div>

        {/* Reto diario */}
        {dailyChallenge && (
          <div className="challenge-card">
            <div className="challenge-header">
              <span className="challenge-icon">⚡</span>
              <div>
                <h4>{dailyChallenge.title}</h4>
                <p>{dailyChallenge.description}</p>
              </div>
            </div>
            <div className="challenge-progress">
              <div className="challenge-bar">
                <div 
                  className="challenge-fill" 
                  style={{ width: `${(dailyChallenge.progress / dailyChallenge.total) * 100}%` }}
                ></div>
              </div>
              <span>{dailyChallenge.progress}/{dailyChallenge.total}</span>
            </div>
            <div className="challenge-footer">
              <span className="challenge-reward">🏆 +{dailyChallenge.reward} XP</span>
              {!isAuthenticated && (
                <button className="continue-btn" onClick={() => setShowLoginPrompt(true)}>
                  <FiLock /> Inicia sesión
                </button>
              )}
            </div>
          </div>
        )}
      </section>
      )}

      {/* Cursos en progreso (solo logueado) */}
      {isAuthenticated && (
      <section className="in-progress-section">
        <div className="section-header">
          <h2><FiPlay /> Continúa aprendiendo</h2>
          <Link to="/academy/my-courses" className="see-all">
            Ver todos <FiChevronRight />
          </Link>
        </div>

        <div className="progress-courses">
          {courses.filter(c => c.completedLessons > 0 && !c.completed).slice(0, 3).map(course => (
            <Link key={course.id} to={`/academy/course/${course.slug || course.id}`} className="progress-course-card">
              <div className="course-image">
                <img src={course.image} alt={course.title} />
                <div className="course-progress-ring">
                  <svg viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                      strokeDasharray={`${getProgressPercentage(course.completedLessons, course.lessons)}, 100`}
                    />
                  </svg>
                  <span>{getProgressPercentage(course.completedLessons, course.lessons)}%</span>
                </div>
              </div>
              <div className="course-info">
                <h3>{course.title}</h3>
                <p>Lección {course.completedLessons + 1} de {course.lessons}</p>
                <button className="continue-btn">
                  <FiPlay /> Continuar
                </button>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* Categorías */}
      <section className="categories-section">
        <div className="categories-scroll">
          {categories.map(cat => {
            const IconComponent = cat.icon;
            return (
              <button
                key={cat.id}
                className={`category-chip ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
                style={{ '--cat-color': cat.color }}
              >
                <IconComponent />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Cursos destacados */}
      <section className="featured-section">
        <div className="section-header">
          <h2><FiStar /> Cursos destacados</h2>
        </div>

        <div className="featured-grid">
          {courses.filter(c => c.featured).map(course => (
            <Link key={course.id} to={`/academy/course/${course.slug || course.id}`} className="featured-course-card">
              <div className="featured-image">
                <img src={course.image} alt={course.title} />
                <div className="featured-overlay">
                  <span className="course-category">{categories.find(c => c.id === course.category)?.name}</span>
                  {course.hasGame && <span className="game-badge">🎮 Con juegos</span>}
                </div>
              </div>
              <div className="featured-content">
                <h3>{course.title}</h3>
                <p>{course.description}</p>
                <div className="course-meta">
                  <span><FiClock /> {course.duration}</span>
                  <span><FiBook /> {course.lessons} lecciones</span>
                  <span><FiStar /> {course.rating}</span>
                </div>
                <div className="course-footer">
                  <span className="instructor">Creado por Graphyra</span>
                  <span className="xp-reward"><FiZap /> +{course.xpReward} XP</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Todos los cursos */}
      <section className="all-courses-section">
        <div className="section-header">
          <h2><FiBook /> Explora cursos</h2>
          <span className="course-count">{filteredCourses.length} cursos</span>
        </div>

        <div className="courses-grid">
          {filteredCourses.map(course => (
            <Link key={course.id} to={`/academy/course/${course.slug || course.id}`} className="course-card">
              <div className="course-thumbnail">
                <img src={course.image} alt={course.title} />
                {course.completed && (
                  <div className="completed-badge">
                    <FiCheck /> Completado
                  </div>
                )}
                {course.completedLessons > 0 && !course.completed && (
                  <div className="progress-badge">
                    {getProgressPercentage(course.completedLessons, course.lessons)}%
                  </div>
                )}
                {course.hasGame && <span className="game-tag">🎮</span>}
              </div>
              <div className="course-body">
                <div className="course-header">
                  <span 
                    className="category-tag"
                    style={{ background: categories.find(c => c.id === course.category)?.color }}
                  >
                    {categories.find(c => c.id === course.category)?.name}
                  </span>
                  <span className="difficulty">{course.difficulty}</span>
                </div>
                <h3>{course.title}</h3>
                <p>{course.description}</p>
                <div className="course-stats">
                  <span><FiUsers /> {course.enrolled.toLocaleString()}</span>
                  <span><FiStar /> {course.rating}</span>
                  <span><FiZap /> {course.xpReward} XP</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Juegos educativos */}
      <section className="games-section">
        <div className="section-header">
          <h2>🎮 Aprende jugando</h2>
          <Link to="/games?category=educational" className="see-all">
            Ver todos <FiChevronRight />
          </Link>
        </div>

        <div className="edu-games-grid">
          <Link to="/games/quiz-master" className="edu-game-card">
            <div className="game-icon">🧠</div>
            <h3>Quiz Master</h3>
            <p>Pon a prueba tus conocimientos</p>
            <span className="game-xp">+50 XP por partida</span>
          </Link>
          <Link to="/games/word-puzzle" className="edu-game-card">
            <div className="game-icon">📝</div>
            <h3>Palabra Maestra</h3>
            <p>Mejora tu vocabulario</p>
            <span className="game-xp">+30 XP por partida</span>
          </Link>
          <Link to="/games/math-rush" className="edu-game-card">
            <div className="game-icon">➕</div>
            <h3>Math Rush</h3>
            <p>Velocidad matemática</p>
            <span className="game-xp">+40 XP por partida</span>
          </Link>
          <Link to="/games/memory-cards" className="edu-game-card">
            <div className="game-icon">🃏</div>
            <h3>Memory Cards</h3>
            <p>Entrena tu memoria</p>
            <span className="game-xp">+25 XP por partida</span>
          </Link>
        </div>
      </section>

      {/* Modal de Racha */}
      {showStreakModal && (
        <div className="modal-overlay" onClick={() => setShowStreakModal(false)}>
          <div className="streak-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowStreakModal(false)}>×</button>
            <div className="streak-fire">🔥</div>
            <h2>¡{userStats.streak} días de racha!</h2>
            <p>Sigue aprendiendo cada día para mantener tu racha</p>
            <div className="streak-calendar">
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
                <div key={i} className={`calendar-day ${i < userStats.streak % 7 ? 'completed' : ''}`}>
                  <span>{day}</span>
                  {i < userStats.streak % 7 && <FiCheck />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Academy;
